import { ClipboardDocumentIcon } from '@heroicons/react/16/solid';
import { useState } from 'react';

import {
  ContactHandle,
  ContactHandleType,
} from '../../../services/types/treatment-types';

interface ContactHandleDisplayProps {
  handle: ContactHandle;
  displayMode?: 'full' | 'valueOnly';
  showType?: boolean;
  compact?: boolean;
}

export const ContactHandleDisplay = ({
  handle,
  displayMode = 'valueOnly',
  showType = false,
  compact = false,
}: ContactHandleDisplayProps) => {
  const [copied, setCopied] = useState(false);

  const getTypeConfig = () => {
    switch (handle.handle_type) {
      case ContactHandleType.PHONE:
        return {
          label: 'phone',
          protocol: 'tel:',
          color: 'text-blue-600',
        };
      case ContactHandleType.EMAIL:
        return {
          label: 'email',
          protocol: 'mailto:',
          color: 'text-green-600',
        };
      case ContactHandleType.WEBSITE:
        return {
          label: 'web',
          protocol: '',
          color: 'text-purple-600',
        };
      case ContactHandleType.FAX:
        return {
          label: 'fax',
          protocol: 'tel:',
          color: 'text-amber-600',
        };
      case ContactHandleType.MAILING_ADDRESS:
        return {
          label: 'mailing address',
          protocol: '',
          color: 'text-zinc-600',
        };
      default:
        return {
          label: 'other',
          protocol: '',
          color: 'text-zinc-600',
        };
    }
  };

  const config = getTypeConfig();

  const getRawValue = (): string => {
    // Return the raw value for copying to clipboard
    return handle.value;
  };

  const getHref = () => {
    if (handle.handle_type === ContactHandleType.WEBSITE) {
      return handle.value.startsWith('http')
        ? handle.value
        : `https://${handle.value}`;
    }
    return `${config.protocol}${handle.value}`;
  };

  const formatValue = (value: string): string => {
    if (
      handle.handle_type === ContactHandleType.PHONE ||
      handle.handle_type === ContactHandleType.FAX
    ) {
      // Attempt to format phone/fax numbers consistently
      const digits = value.replace(/\D/g, '');
      if (digits.length === 10) {
        return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
      }
      if (digits.length === 11 && digits.startsWith('1')) {
        return `+1 (${digits.substring(1, 4)}) ${digits.substring(4, 7)}-${digits.substring(7)}`;
      }
      if (digits.length === 7) {
        return `${digits.substring(0, 3)}-${digits.substring(3)}`;
      }
    } else if (handle.handle_type === ContactHandleType.WEBSITE) {
      // Clean up website URLs for display
      const cleanUrl = value
        .replace(/^https?:\/\/(www\.)?/, '')
        .replace(/\/$/, '');

      // Truncate long URLs
      if (cleanUrl.length > 30) {
        const parts = cleanUrl.split('/');
        if (parts.length > 1) {
          // For paths, show domain and truncate the path
          return `${parts[0]}/...`;
        }
        // For long domains, truncate in the middle
        return `${cleanUrl.substring(0, 15)}...${cleanUrl.substring(cleanUrl.length - 10)}`;
      }
      return cleanUrl;
    }
    return value;
  };

  const copyToClipboard = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    navigator.clipboard
      .writeText(getRawValue())
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch((err) => {
        console.error('Failed to copy text: ', err);
      });
  };

  const isLinkable =
    handle.handle_type === ContactHandleType.WEBSITE ||
    handle.handle_type === ContactHandleType.EMAIL ||
    handle.handle_type === ContactHandleType.PHONE;

  const renderTypeLabel = () => {
    if (!showType) return null;
    return (
      <span className={`${config.color} text-xs font-semibold mr-1.5`}>
        {config.label}:
      </span>
    );
  };

  const formattedValue = formatValue(handle.value);

  const fontSizeClass = compact ? 'text-xs' : 'text-sm';
  const fontWeightClass = 'font-medium';
  const copyIconClass = compact ? 'h-3 w-3' : 'h-3.5 w-3.5';
  const copyMarginClass = compact ? 'ml-1' : 'ml-1.5';

  // Use a more muted link color for phone numbers
  const getLinkColorClass = () => {
    return 'text-blue-700 font-medium';
  };

  return (
    <span className="inline-flex items-center group">
      {isLinkable ? (
        <a
          href={getHref()}
          target={
            handle.handle_type === ContactHandleType.WEBSITE
              ? '_blank'
              : undefined
          }
          rel={
            handle.handle_type === ContactHandleType.WEBSITE
              ? 'noopener noreferrer'
              : undefined
          }
          className={`hover:underline ${getLinkColorClass()} ${fontWeightClass} ${fontSizeClass}`}
        >
          {showType && renderTypeLabel()}
          {formattedValue}
        </a>
      ) : (
        <span className={`text-zinc-700 ${fontWeightClass} ${fontSizeClass}`}>
          {showType && renderTypeLabel()}
          {formattedValue}
        </span>
      )}
      <button
        type="button"
        onClick={copyToClipboard}
        className={`${copyMarginClass} text-zinc-300 hover:text-zinc-500 focus:text-blue-500 transition-all ${copied ? 'text-green-500' : 'opacity-0 group-hover:opacity-100'}`}
        title="Copy to clipboard"
        aria-label="Copy to clipboard"
      >
        <ClipboardDocumentIcon className={copyIconClass} />
      </button>
    </span>
  );
};
